import { z } from 'zod';
import { IdSchema } from './common.js';
import { StreamModeSchema } from './modes.js';
/**
 * Binary envelope used on every WebSocket channel for bulk payloads:
 *
 *   [u8 kind][u16 BE headerLen][header JSON (utf-8)][payload bytes]
 *
 * kind 1 = video frame (JPEG), kind 2 = file upload chunk.
 * The server forwards frames verbatim (no decode/re-encode); only the header is parsed when needed.
 */
export const BinaryKind = {
    Frame: 1,
    FileChunk: 2,
};
export const FrameMetaSchema = z.object({
    /** CSS pixel size of the viewport the frame depicts (may differ from image pixel size) */
    deviceWidth: z.number(),
    deviceHeight: z.number(),
    scrollX: z.number().default(0),
    scrollY: z.number().default(0),
    /** Node-side timestamp (ms since epoch) */
    ts: z.number(),
});
export const FrameHeaderSchema = z.object({
    profileId: IdSchema,
    tabId: IdSchema,
    /** Monotonic per-tab sequence, used by viewers to drop stale frames */
    seq: z.number().int().min(0),
    /** Image pixel size */
    w: z.number().int().min(1),
    h: z.number().int().min(1),
    /** MIME of payload; screencast uses image/jpeg */
    mime: z.string().default('image/jpeg'),
    /** transport mode that produced the frame */
    mode: StreamModeSchema.default('screencast'),
    meta: FrameMetaSchema,
});
export const FileChunkHeaderSchema = z.object({
    /** Which file chooser request this chunk belongs to */
    chooserId: IdSchema,
    /** File index within a multi-file selection */
    fileIndex: z.number().int().min(0).default(0),
    /** Chunk index within the file, starting at 0 */
    chunkIndex: z.number().int().min(0),
    /** Optional routing fields, present on the node channel (server fills them in) */
    profileId: IdSchema.optional(),
    tabId: IdSchema.optional(),
});
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
export function encodeBinary(kind, header, payload) {
    const headerBytes = textEncoder.encode(JSON.stringify(header));
    if (headerBytes.byteLength > 0xffff)
        throw new Error('binary header too large');
    const out = new Uint8Array(3 + headerBytes.byteLength + payload.byteLength);
    out[0] = kind;
    out[1] = (headerBytes.byteLength >> 8) & 0xff;
    out[2] = headerBytes.byteLength & 0xff;
    out.set(headerBytes, 3);
    out.set(payload, 3 + headerBytes.byteLength);
    return out;
}
export function decodeBinary(buf) {
    if (buf.byteLength < 3)
        throw new Error('binary message too short');
    const kind = buf[0];
    const headerLen = ((buf[1] << 8) | buf[2]) >>> 0;
    if (buf.byteLength < 3 + headerLen)
        throw new Error('binary header truncated');
    const headerJson = textDecoder.decode(buf.subarray(3, 3 + headerLen));
    const header = JSON.parse(headerJson);
    const payload = buf.subarray(3 + headerLen);
    return { kind, header, payload };
}
/** Cheap peek at the kind byte without parsing the header. */
export function peekBinaryKind(buf) {
    return buf.byteLength >= 1 ? buf[0] : undefined;
}
export function encodeFrame(header, jpeg) {
    return encodeBinary(BinaryKind.Frame, header, jpeg);
}
export function decodeFrame(buf) {
    const d = decodeBinary(buf);
    if (d.kind !== BinaryKind.Frame)
        throw new Error(`expected frame, got kind ${d.kind}`);
    return { header: FrameHeaderSchema.parse(d.header), payload: d.payload };
}
export function encodeFileChunk(header, bytes) {
    return encodeBinary(BinaryKind.FileChunk, header, bytes);
}
export function decodeFileChunk(buf) {
    const d = decodeBinary(buf);
    if (d.kind !== BinaryKind.FileChunk)
        throw new Error(`expected file chunk, got kind ${d.kind}`);
    return { header: FileChunkHeaderSchema.parse(d.header), payload: d.payload };
}
//# sourceMappingURL=binary.js.map